import type { FeatureKey, MemberRole, PlanId, SubscriptionStatus } from "@/lib/api/types";

export const PLAN_ORDER: PlanId[] = ["academy", "driver", "navigator", "oracle"];

export const PLAN_LABELS: Record<PlanId, string> = {
  academy: "Academy",
  driver: "Driver",
  navigator: "Navigator",
  oracle: "Oracle",
};

export function planRank(plan: PlanId | string | null | undefined): number {
  const index = PLAN_ORDER.indexOf(plan as PlanId);
  return index === -1 ? 0 : index;
}

export function planLabel(plan: PlanId | string | null | undefined): string {
  return PLAN_LABELS[plan as PlanId] ?? "Rocketeer Orbit";
}

/**
 * The API's feature vocabulary, taken from `GET /v1/reference/plans` rather than
 * guessed: metrics, valuation, todos, portfolio, education_library, rai_chat,
 * rai_tools, rai_file_context, file_upload, masterclass_vault,
 * investor_intelligence, human_navigator, strategy_sessions, ai_crew,
 * team_seats, quarterly_intensives, vip_events.
 */
export const FEATURE_LABELS: Partial<Record<FeatureKey, string>> = {
  metrics: "Monthly figures",
  valuation: "Business valuation",
  todos: "Tasks & focus",
  portfolio: "Products & customers",
  education_library: "Academy library",
  masterclass_vault: "Masterclass vault",
  rai_chat: "RAI adviser",
  rai_tools: "RAI can act for you",
  rai_file_context: "RAI reads your documents",
  file_upload: "Document vault",
  investor_intelligence: "Investor intelligence",
  human_navigator: "A human Navigator",
  strategy_sessions: "Strategy sessions",
  ai_crew: "AI crew",
  team_seats: "Team seats",
  quarterly_intensives: "Quarterly intensives",
  vip_events: "VIP events",
};

export function featureLabel(key: FeatureKey): string {
  return FEATURE_LABELS[key] ?? key.replace(/[_-]+/g, " ");
}

/**
 * The UI thinks in areas ("can they use RAI?"); the API answers in the keys
 * above. This maps one to the other, so a rename on either side is a one-line
 * change rather than a feature silently vanishing from the sidebar.
 *
 * An empty list means "not plan-gated at all" — gamification has no key in the
 * vocabulary and api.md §7 attaches no plan requirement to it.
 */
const FEATURE_ALIASES: Record<string, FeatureKey[]> = {
  metrics: ["metrics"],
  valuation: ["valuation"],
  todos: ["todos"],
  portfolio: ["portfolio"],
  rai: ["rai_chat", "rai_tools", "rai_file_context"],
  files: ["file_upload", "rai_file_context"],
  education: ["education_library", "masterclass_vault"],
  navigator: ["human_navigator"],
  gamification: [],
  companies_house: [],
};

export function matchesFeature(features: FeatureKey[], key: FeatureKey): boolean {
  const aliases = FEATURE_ALIASES[key];
  if (!aliases) return features.includes(key);
  if (aliases.length === 0) return true;
  // Accept the canonical key too, in case the API ever adopts the shorter name.
  return aliases.some((alias) => features.includes(alias)) || features.includes(key);
}

export const ROLE_ORDER: MemberRole[] = ["viewer", "member", "admin", "owner"];

export const ROLE_LABELS: Record<MemberRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

export function roleRank(role: MemberRole | null | undefined): number {
  const index = ROLE_ORDER.indexOf(role as MemberRole);
  return index === -1 ? -1 : index;
}

export function roleAtLeast(role: MemberRole | null | undefined, minimum: MemberRole): boolean {
  return roleRank(role) >= roleRank(minimum);
}

/**
 * Billing state, read the way api.md prescribes:
 * - gate features on `features[]`, never on `plan`
 * - `effectivePlan !== plan` means the subscription lapsed → fix-your-billing
 * - `past_due` keeps full access (Stripe is still retrying) → don't degrade
 */
export function subscriptionState(
  sub:
    | {
        plan?: PlanId;
        effectivePlan?: PlanId;
        status?: SubscriptionStatus;
        cancelAtPeriodEnd?: boolean;
      }
    | null
    | undefined,
) {
  if (!sub) {
    return {
      lapsed: false,
      retrying: false,
      needsCheckout: false,
      showBillingWarning: false,
      cancelling: false,
    };
  }

  // Only a plan mismatch we can actually see counts as lapsed — a missing
  // effectivePlan means we weren't told, not that something is wrong.
  const lapsed = Boolean(sub.effectivePlan && sub.plan && sub.effectivePlan !== sub.plan);
  const retrying = sub.status === "past_due";
  const needsCheckout = sub.status === "unpaid" || sub.status === "incomplete" || sub.status === "none";
  return {
    lapsed,
    retrying,
    needsCheckout,
    /** Show a banner, but never take the UI away for `past_due` alone. */
    showBillingWarning: lapsed || retrying || needsCheckout,
    cancelling: Boolean(sub.cancelAtPeriodEnd),
  };
}

export const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Payment retrying",
  unpaid: "Unpaid",
  canceled: "Cancelled",
  incomplete: "Incomplete",
  incomplete_expired: "Expired",
  paused: "Paused",
  none: "No subscription",
};

export function statusLabel(status: SubscriptionStatus | null | undefined): string {
  if (!status) return "Unknown";
  return STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}
